import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import './index.css';
import './styles/global.css';
import App from './App';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { auth } from './config/firebase';
import { onAuthStateChanged } from '@firebase/auth';
// RBAC removed - debugPermissions deleted

// Import build version (will be generated during build)
let BUILD_VERSION = 'dev';
try {
  const buildInfo = require('./buildVersion');
  BUILD_VERSION = buildInfo.BUILD_VERSION;
  console.log('Build version:', BUILD_VERSION);
} catch (e) {
  console.log('Development mode - no build version');
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Register service worker for PWA
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      // Prevent multiple registrations in short time period
      const lastRegistration = localStorage.getItem('last_sw_registration');
      const now = new Date().getTime();

      if (lastRegistration && (now - parseInt(lastRegistration)) < 5 * 60 * 1000) {
        console.log('Skipping service worker registration - recently registered');
        return;
      }

      // Record registration time
      localStorage.setItem('last_sw_registration', now.toString());

      // Add version to service worker URL to bust cache
      navigator.serviceWorker.register(`/service-worker.js?v=${BUILD_VERSION}`)
        .then(registration => {
          console.log('✅ Service worker registered:', registration);

          // Set up message channel for communication
          if (registration.active) {
            const messageChannel = new MessageChannel();
            messageChannel.port1.onmessage = (event) => {
              if (event.data.version) {
                console.log('Service worker version:', event.data.version);
              }
            };
            registration.active.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
          }

          // Handle service worker updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version available
                  console.log('🆕 New service worker version available');
                  // Could show update notification here
                } else if (newWorker.state === 'activated') {
                  console.log('✅ Service worker updated successfully');
                }
              });
            }
          });
        })
        .catch(error => {
          console.error('❌ Service worker registration failed:', error);
        });
    });
  } else {
    console.log('Service workers not supported in this browser');
  }
};

// Call service worker registration
registerServiceWorker();

// Service worker registration is now handled above
// No need to unregister existing service workers as the new version will replace them

// Add an event listener to handle browser-specific cache busting on page load
window.addEventListener('load', () => {
  // Add a timestamp to all script and CSS resources
  const timestamp = new Date().getTime();
  const styleSheets = document.styleSheets;
  const scripts = document.scripts;
  
  // This helps force Safari to reload resources
  try {
    for (let i = 0; i < styleSheets.length; i++) {
      if (styleSheets[i].href) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = styleSheets[i].href.split('?')[0] + '?t=' + timestamp;
        document.head.appendChild(link);
      }
    }
  } catch (e) {
    console.error('Error updating stylesheets:', e);
  }
  
  console.log('Cache busting applied to page resources');
});

// Function to clear application cache
window.clearAppCache = () => {
  console.log('Manual cache clear requested');
  
  // Simple refresh without complex cache clearing to prevent refresh loops
  const refreshPage = () => {
    console.log('Performing simple page refresh');
    window.location.reload();
  };
  
  // Very basic cache clearing that won't cause loops
  if ('caches' in window) {
    try {
      console.log('Attempting to clear caches');
      caches.keys()
        .then(cacheNames => {
          console.log('Found caches:', cacheNames);
          // Only proceed with page refresh, skip actual cache deletion for now
          refreshPage();
        })
        .catch(err => {
          console.error('Error accessing caches:', err);
          refreshPage();
        });
    } catch (e) {
      console.error('Error in cache clearing:', e);
      refreshPage();
    }
  } else {
    console.log('Cache API not available');
    refreshPage();
  }
};

// Add a version meta tag to the document
const metaVersion = document.createElement('meta');
metaVersion.name = 'app-version';
metaVersion.content = BUILD_VERSION;
document.head.appendChild(metaVersion);

// Get the base URL from the current location
const getBasename = () => {
  // For local development, always use empty string
  if (window.location.hostname === 'localhost') {
    return '';
  }
  
  // For production, use a consistent basename approach
  // Extract the first path segment if it exists
  try {
    const pathSegments = window.location.pathname.split('/');
    if (pathSegments.length > 1 && pathSegments[1]) {
      // Ensure the basename is properly formatted with a leading slash
      return '/' + pathSegments[1];
    }
  } catch (error) {
    console.error('Error determining basename:', error);
  }
  
  // Default to empty string if we can't determine a basename
  return '';
};

// Initialize the app
const initApp = () => {
  try {
    // For local development, use HashRouter instead of BrowserRouter
    // This avoids the "operation is insecure" error that can occur with BrowserRouter
    const isLocalhost = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    
    // Render app with the appropriate router
    root.render(
      <React.StrictMode>
        <FeatureFlagsProvider>
          {isLocalhost ? (
            <HashRouter>
              <App />
            </HashRouter>
          ) : (
            <BrowserRouter>
              <App />
            </BrowserRouter>
          )}
        </FeatureFlagsProvider>
      </React.StrictMode>
    );

    // Set up auth state listener
    onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('User is signed in:', user.uid);
      } else {
        console.log('User is signed out');
      }
    });
  } catch (error) {
    console.error('Failed to initialize app:', error);
    root.render(
      <div className="error-container">
        <h1>Application Error</h1>
        <p>Failed to initialize the application. Please try again later.</p>
        <button onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }
};

// Start the application
initApp();
